// One asset page: everything about one asset, read only, laid out like the workbook row.
// Words: ONE_ASSET_PAGE_WORDS.md. Mockup: MOCKUP_ONE_ASSET_PAGE.html (approved 2026-10-24, 7:30 AM).
//
// Rules:
// * Read only. The Asset ID Creator stays the one place an asset is made, changed or removed (rule 31).
// * Left: the row in workbook column order (Identity, Shock, Arc Flash, Condition, Labels, Change History).
//   Right: where it sits (Fed From, Feeds, Sits In, Ways Out), open records, and the way to change it.
// * Values show as the record holds them. Shock calcs are not worked out again.
// * Red is overduty only.
// Pure: data in, view out.

import { housingOf } from "./survey";

/** Everything the page reads for one asset. Null parts were not readable or not there. */
export interface AssetPageData {
  assetId: string;
  siteNumber: string;
  siteName: string;
  /** The asset record as stored. */
  doc: Record<string, unknown>;
  /** Every asset ID at the site, for Sits In and Ways Out. */
  siteIds: string[];
  /** Change records for this asset, any order. */
  changes: Record<string, unknown>[];
  /** The IEA record, null when none. Undefined when the badge has no IEA Site Survey switch. */
  iea?: Record<string, unknown> | null;
  /** Connections touching this asset. Undefined without the IEA Site Survey switch. */
  links?: { from: string; to: string; status: string }[];
  /** The published arc flash label, null when none. */
  afLabel: Record<string, unknown> | null;
  /** The study the asset's numbers came from, when it has one. */
  study: { studyId: string; firm: string; jobNumber: string; assessmentDate: string; state: string; labelsAt: string } | null;
  /** Open work requests and near misses on this asset. */
  records: { kind: "Work Request" | "Near Miss"; id: string; line: string; href: string; priority: string }[];
}

export interface ARow {
  k: string;
  v: string;
  /** A second, quieter line under the value. */
  sub?: string;
  calc?: boolean;
  /** RPN band: low, med, high. */
  band?: "low" | "med" | "high" | "";
  /** A pill instead of plain text. */
  pill?: { tone: "green" | "amber" | "red" | "grey"; text: string };
  mono?: boolean;
  link?: { href: string; text: string; newTab?: boolean };
}

export interface AssetView {
  assetId: string;
  siteNumber: string;
  sub: string;
  outOfService: boolean;
  removed: boolean;
  identity: ARow[];
  shock: ARow[];
  arcFlash: ARow[];
  condition: ARow[];
  labels: ARow[];
  history: { title: string; detail: string; who: string }[];
  fedFrom: string[];
  feeds: string[];
  sitsIn: string | null;
  waysOut: string[];
  /** "Needs the IEA Site Survey switch" when the badge cannot read the map. */
  mapNote: string;
  records: AssetPageData["records"];
}

const s = (v: unknown): string => (typeof v === "string" ? v : typeof v === "number" ? String(v) : "");
const yn = (v: unknown): string => (v === true ? "YES" : v === false ? "NO" : "");
const NOT = "Not on record";

/** "2026-10-22T14:14:00Z" or "2026-10-22" → "10/22/2026". */
export function usDay(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[2]}/${m[3]}/${m[1]}` : iso;
}

export function rpnBandOf(rpn: unknown): "low" | "med" | "high" | "" {
  if (typeof rpn !== "number") return "";
  return rpn <= 3 ? "low" : rpn <= 7 ? "med" : "high";
}

/** The Equipment Location and Location Number, read back from "Electrical Room: ER1". */
export function splitLocation(location: string): { loc: string; num: string } {
  const i = location.indexOf(":");
  return i < 0 ? { loc: location, num: "" } : { loc: location.slice(0, i).trim(), num: location.slice(i + 1).trim() };
}

/** The part of the Asset ID after the housing: its "END" Location. Blank is the housing itself. */
export function endLocationOf(assetId: string, sitsIn: string | null): string {
  return sitsIn ? assetId.slice(sitsIn.length + 1) : "";
}

/** The Equipment Number: the last part of the ID before any END location. */
function equipmentNumberOf(assetId: string, sitsIn: string | null): string {
  const own = sitsIn ?? assetId;
  return own.split("-").pop() ?? "";
}

const FIELD_NAMES: Record<string, string> = {
  isOutOfService: "Out of Service", nominalVoltage: "Nominal System Voltage", nominalVolts: "Volts", conductorType: "Conductor",
  hasShockHazard: "Shock Hazard", hasPassiveExposure: "Passive Exposure", hasActiveExposure: "Active Exposure",
  isProperlyInstalled: "Properly Installed", isUsedPerInstructions: "Used Per Instructions", shockLikelihood: "Likelihood",
  shockSeverity: "Severity", shockRpn: "RPN", limitedApproach: "Limited Approach", restrictedApproach: "Restricted Approach",
  isShockProtectionRequired: "Shock Protection Required", shockResidualRpn: "Residual RPN", isRemoved: "Removed",
  location: "Location", equipmentType: "Equipment Type", equipmentCode: "Class",
};
const shown = (v: unknown): string => (v === true ? "YES" : v === false ? "NO" : v === null || v === undefined || v === "" ? "blank" : String(v));

/** One change record in words: "Active Exposure YES to NO". Derived values that follow an answer are left out. */
export function changeLine(c: Record<string, unknown>): { title: string; detail: string; who: string } {
  const action = s(c.Action);
  const changes = (c.Changes && typeof c.Changes === "object" ? c.Changes : {}) as Record<string, { before?: unknown; after?: unknown }>;
  const DERIVED = new Set(["nominalVolts", "hasShockHazard", "shockLikelihood", "shockSeverity", "shockRpn", "limitedApproach", "restrictedApproach", "isShockProtectionRequired", "shockResidualRpn", "equipmentCode"]);
  const parts = Object.entries(changes)
    .filter(([k]) => !DERIVED.has(k) && FIELD_NAMES[k])
    .map(([k, v]) => `${FIELD_NAMES[k]} ${shown(v?.before)} to ${shown(v?.after)}`);
  const who = [usDay(s(c["Changed At"])), s(c["Holder Name"]) || s(c["Badge ID"])].filter(Boolean).join(" · ");
  const title =
    action === "Created" ? "Made" : action === "Removed" ? "Removed" : action === "Restored" ? "Brought Back" : action === "Label Printed" ? "QR Label Printed" : action === "Dropped" ? "Dropped" : "Changed";
  const note = s(c.Note);
  return { title, detail: [parts.join(", "), note].filter(Boolean).join(" · ") || (title === "Made" ? "In the Assessment Sheet" : ""), who };
}

export function assetView(d: AssetPageData): AssetView {
  const doc = d.doc;
  const ids = new Set(d.siteIds);
  const sitsIn = housingOf(d.assetId, ids);
  const waysOut = d.siteIds.filter((x) => x !== d.assetId && housingOf(x, ids) === d.assetId).sort((a, b) => a.localeCompare(b, "en", { numeric: true }));
  const where = splitLocation(s(doc.location));
  const type = s(doc.equipmentType);
  const code = s(doc.equipmentCode);
  const end = endLocationOf(d.assetId, sitsIn);

  const identity: ARow[] = [
    { k: "Equipment Location", v: where.loc || NOT },
    { k: "Location Number", v: where.num || "None", mono: !!where.num },
    { k: "Equipment Type", v: type || NOT, sub: code ? `Class ${code}` : undefined },
    { k: "Equipment Number", v: equipmentNumberOf(d.assetId, sitsIn) || NOT, mono: true },
    { k: '"END" Location', v: end || "The housing itself", mono: !!end },
    { k: "Nominal System Voltage", v: s(doc.nominalVoltage) || NOT },
    { k: "Out of Service", v: yn(doc.isOutOfService) || "NO" },
  ];

  const lik = doc.shockLikelihood;
  const sev = doc.shockSeverity;
  const shock: ARow[] = [
    { k: "Conductor", v: s(doc.conductorType) || NOT },
    { k: "Passive Exposure", v: yn(doc.hasPassiveExposure) || NOT },
    { k: "Active Exposure", v: yn(doc.hasActiveExposure) || NOT },
    { k: "Properly Installed", v: yn(doc.isProperlyInstalled) || NOT },
    { k: "Used Per Instructions", v: yn(doc.isUsedPerInstructions) || NOT },
    { k: "Shock Hazard", v: yn(doc.hasShockHazard) || NOT, calc: true },
    { k: "Likelihood × Severity", v: typeof lik === "number" && typeof sev === "number" ? `${lik} × ${sev}` : NOT, calc: true },
    { k: "RPN", v: s(doc.shockRpn) || NOT, band: rpnBandOf(doc.shockRpn), calc: true },
    { k: "Limited Approach", v: s(doc.limitedApproach) || NOT, calc: true },
    { k: "Restricted Approach", v: s(doc.restrictedApproach) || NOT, calc: true },
    { k: "Shock Protection Required", v: yn(doc.isShockProtectionRequired) || NOT, calc: true },
    { k: "Residual RPN", v: s(doc.shockResidualRpn) || NOT, band: rpnBandOf(doc.shockResidualRpn), calc: true },
  ];

  const ie = doc.incidentEnergyCal;
  const afb = doc.arcFlashBoundary;
  const label = d.afLabel ? s(d.afLabel.Label_Type) : "";
  const overduty = doc.isOverduty === true || label === "OVERDUTY";
  const arcFlash: ARow[] = [
    { k: "Incident Energy", v: typeof ie === "number" ? `${ie} cal/cm²` : s(ie) || "Waits on the study" },
    { k: "Arc Flash Boundary", v: typeof afb === "number" ? `${afb} in.` : s(afb) || "Waits on the study" },
    { k: "IE Status", v: s(doc.ieStatus) || "IE pending study" },
    d.study
      ? { k: "Study", v: `${d.study.firm} · Job ${d.study.jobNumber || "none"}`, sub: `Assessed ${d.study.assessmentDate}`, link: { href: `#/af-labels/${d.siteNumber}/s/${d.study.studyId}`, text: "Open Study" } }
      : { k: "Study", v: "No study yet" },
    { k: "Label", v: label || "None", pill: label ? { tone: label === "DANGER" || label === "OVERDUTY" ? "red" : "amber", text: label } : undefined },
    { k: "Overduty", v: overduty ? "YES" : "NO", pill: overduty ? { tone: "red", text: "OVERDUTY" } : undefined },
  ];

  const condition: ARow[] =
    d.iea === undefined
      ? [{ k: "IEA", v: "Needs the IEA Site Survey switch" }]
      : d.iea === null
        ? [{ k: "IEA", v: "None yet", link: { href: `#/survey/${d.siteNumber}/a/${encodeURIComponent(d.assetId)}`, text: "Open In Survey" } }]
        : [
            {
              k: "IEA",
              v: "Saved",
              sub: [usDay(s(d.iea["Recorded At"])), s(d.iea["Holder Name"]), photoLine(d.iea.Photos)].filter(Boolean).join(" · "),
              link: { href: `#/survey/${d.siteNumber}/a/${encodeURIComponent(d.assetId)}`, text: "Open In Survey" },
            },
          ];

  const printed = s(doc.qrLabelPrintedAt);
  const labels: ARow[] = [
    { k: "QR Label", v: printed ? "Printed" : "Not printed", sub: printed ? usDay(printed) : undefined },
    { k: "Arc Flash Label", v: d.study?.labelsAt && label ? "Created" : "Not created", sub: d.study?.labelsAt && label ? `${usDay(d.study.labelsAt)} · ${label} set` : undefined },
  ];

  const history = [...d.changes]
    .sort((a, b) => s(b["Changed At"]).localeCompare(s(a["Changed At"])))
    .map(changeLine);
  if (!history.some((h) => h.title === "Made") && s(doc.createdAt))
    history.push({ title: "Made", detail: s(doc.source) === "Asset ID page" ? "In the Assessment Sheet" : "Copied from the Sheet", who: [usDay(s(doc.createdAt)), s(doc.createdByName)].filter(Boolean).join(" · ") });

  const live = (d.links ?? []).filter((l) => l.status !== "Removed");
  return {
    assetId: d.assetId,
    siteNumber: d.siteNumber,
    sub: [type, s(doc.location), d.siteName].filter(Boolean).join(" · "),
    outOfService: doc.isOutOfService === true,
    removed: doc.isRemoved === true,
    identity,
    shock,
    arcFlash,
    condition,
    labels,
    history,
    fedFrom: [...new Set(live.filter((l) => l.to === d.assetId).map((l) => l.from))].filter(Boolean),
    feeds: [...new Set(live.filter((l) => l.from === d.assetId).map((l) => l.to))].filter(Boolean),
    sitsIn,
    waysOut,
    mapNote: d.links === undefined ? "Needs the IEA Site Survey switch" : "",
    records: d.records,
  };
}

function photoLine(p: unknown): string {
  const n = p && typeof p === "object" ? Object.keys(p as object).length : 0;
  return n ? `${n} ${n === 1 ? "photo" : "photos"}` : "";
}

/** Where a page links to an asset's page. */
export const assetHref = (site: string, assetId: string): string => `#/sites/${site}/assets/${encodeURIComponent(assetId)}`;
